/**
 * ConfirmDialog - Replaces window.confirm() with a proper modal
 *
 * Props:
 *   isOpen      - Whether the dialog is visible
 *   onConfirm   - Called when user confirms
 *   onCancel    - Called when user cancels or clicks backdrop
 *   title       - Dialog title
 *   message     - Dialog message (string or JSX)
 *   confirmText - Text for confirm button (default: "Confirm")
 *   cancelText  - Text for cancel button (default: "Cancel")
 *   variant     - 'danger' | 'warning' | 'info' (default: 'info')
 */
import React from 'react';
import { FiAlertTriangle, FiAlertCircle, FiInfo } from 'react-icons/fi';
import type { IconType } from 'react-icons';

type ConfirmVariant = 'danger' | 'warning' | 'info';

interface VariantConfig {
  icon: IconType;
  iconColor: string;
  buttonClass: string;
}

const VARIANT_CONFIG: Record<ConfirmVariant, VariantConfig> = {
  danger: {
    icon: FiAlertTriangle,
    iconColor: 'text-red-500',
    buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
  warning: {
    icon: FiAlertCircle,
    iconColor: 'text-yellow-500',
    buttonClass: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
  },
  info: {
    icon: FiInfo,
    iconColor: 'text-blue-500',
    buttonClass: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
  },
};

interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

const ConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Confirm',
  message = 'Are you sure?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
}: ConfirmDialogProps) => {
  if (!isOpen) return null;

  const config = VARIANT_CONFIG[variant] ?? VARIANT_CONFIG.info;
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onCancel} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCancel(); }} role="presentation"
        />
        {/* Dialog */}
        <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 ${config.iconColor}`}>
              <Icon size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {title}
              </h3>
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {message}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.buttonClass}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
